import { HttpClient, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { RawMaterialDetailLine } from './models/raw-material-detail-line.model';
import { RawMaterialVariant } from './models/raw-material-variant.model';
import { RawMaterial } from './models/raw-material.model';
import * as PurchasePlannerActions from './purchase-planner.actions';
import { PurchasePlanArchive } from './models/purchase-plan-archive.model';

export const loadRawMaterials$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), http = inject(HttpClient)) => {
    return actions$.pipe(
      ofType(PurchasePlannerActions.loadRawMaterials),
      mergeMap(({ useArchive }) => {
        const params: HttpParams = new HttpParams().set('UseArchive', useArchive);

        return http
          .get<{
            lines: RawMaterial[];
            details: Record<string, RawMaterialDetailLine[]>;
          }>(environment.apiUrl + '/api/PurchasePlan', { params })
          .pipe(
            map((res) => {
              const transformedData: {
                rawMaterials: RawMaterial[];
                details: Record<string, RawMaterialDetailLine[]>;
              } = transformPurchasePlanResponse(res);
              return PurchasePlannerActions.loadRawMaterialsSuccess({
                rawMaterials: transformedData.rawMaterials,
                details: transformedData.details,
              });
            }),
            catchError(error =>
              of(
                PurchasePlannerActions.loadRawMaterialsFailure({
                  error: error.message ?? 'Unknown error loading raw materials',
                })
              )
            )
          );
      })
    );
  },
  { functional: true }
);

export function transformPurchasePlanResponse(response: {
  lines: RawMaterial[];
  details: Record<string, RawMaterialDetailLine[]>;
}): {
  rawMaterials: RawMaterial[];
  details: Record<string, RawMaterialDetailLine[]>;
} {
  const rawMaterials: RawMaterial[] = transformRawMaterials(response.lines);
  const details: Record<string, RawMaterialDetailLine[]> = transformDetails(response.details);

  return { rawMaterials, details };
}

function transformRawMaterials(lines: RawMaterial[]): RawMaterial[] {
  return lines.map((item) => {
    const variants: RawMaterialVariant[] = item.variants ?? [];

    let totalDemand: number = 0;
    let totalRequiredDemand: number = 0;
    let totalPurchaseQuantity: number = 0;

    if (variants.length > 0) {
      totalRequiredDemand = variants.reduce(
        (sum, variant) => sum + (variant.requiredDemand ?? 0),
        0
      );

      totalPurchaseQuantity = variants.reduce(
        (sum, variant) => sum + (variant.purchaseQuantity ?? 0),
        0
      );

      let maxPositiveDemand: number = -Infinity;
      let negativeDemandSum: number = 0;
      let hasPositiveDemand: boolean = false;

      variants.forEach((variant) => {
        const demand: number = variant.demand ?? 0;

        if (demand > 0) {
          hasPositiveDemand = true;
          maxPositiveDemand = Math.max(maxPositiveDemand, demand);
        } else {
          negativeDemandSum += demand;
        }
      });

      totalDemand = hasPositiveDemand ? maxPositiveDemand : negativeDemandSum;
    }

    return {
      itemNumber: item.itemNumber,
      description: item.description ?? '',
      totalDemand: totalDemand,
      totalRequiredDemand: totalRequiredDemand,
      totalPurchaseQuantity: totalPurchaseQuantity,
      productTypes: item.productTypes ?? [],
      variants: variants,
    };
  });
}

function transformDetails(
  details: Record<string, RawMaterialDetailLine[]>
): Record<string, RawMaterialDetailLine[]> {
  const transformed: Record<string, RawMaterialDetailLine[]> = {};

  for (const [key, detailLines] of Object.entries(details)) {
    transformed[key] = detailLines.map(dto => ({
      itemNumber: dto.itemNumber,
      initialDemand: dto.initialDemand,
      finishedGoodsSupply: dto.finishedGoodsSupply,
      rawMaterialSupply: dto.rawMaterialSupply,
      arrivedPurchaseLineSupply: dto.arrivedPurchaseLineSupply,
      confirmedPurchaseLineSupply: dto.confirmedPurchaseLineSupply,
      openPurchaseLineSupply: dto.openPurchaseLineSupply,
      remainingDemand: dto.remainingDemand,
    }));
  }

  return transformed;
}

export const loadArchiveEntries$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), http = inject(HttpClient)) => {
    return actions$.pipe(
      ofType(PurchasePlannerActions.loadArchiveEntries),
      mergeMap(() =>
        http
          .get<{ data: PurchasePlanArchive[]; }>(environment.apiUrl + '/api/PurchasePlanArchive?PageNumber=1&PageSize=100')
          .pipe(
            map((response) => {
              return PurchasePlannerActions.loadArchiveEntriesSuccess({
                archiveEntries: response.data,
              });
            }),
            catchError(error =>
              of(
                PurchasePlannerActions.loadArchiveEntriesFailure({
                  error: error.message ?? 'Unknown error loading archive entries',
                })
              )
            )
          )
      )
    );
  },
  { functional: true }
);

export const loadArchiveData$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), http = inject(HttpClient)) => {
    return actions$.pipe(
      ofType(PurchasePlannerActions.loadArchiveData),
      mergeMap(({ archiveId }) => {
        return http
          .get<{
            lines: RawMaterial[];
            details: Record<string, RawMaterialDetailLine[]>;
          }>(`${environment.apiUrl}/api/PurchasePlanArchive/${archiveId}`)
          .pipe(
            map((res) => {
              const transformedData: {
                rawMaterials: RawMaterial[];
                details: Record<string, RawMaterialDetailLine[]>;
              } = transformPurchasePlanResponse(res);
              return PurchasePlannerActions.loadArchiveDataSuccess({
                rawMaterials: transformedData.rawMaterials,
                details: transformedData.details,
              });
            }),
            catchError(error =>
              of(
                PurchasePlannerActions.loadArchiveDataFailure({
                  error: error.message ?? `Unknown error loading archive data for ID: ${archiveId}`,
                })
              )
            )
          );
      })
    );
  },
  { functional: true }
);

export const purchasePlannerEffects: object = {
  loadRawMaterials$,
  loadArchiveEntries$,
  loadArchiveData$,
};
